import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import {
  Plus, Trash2, Search, X, FileText, Users as UsersIcon, ImageIcon, Mic,
  Music, Palette, Film, Layers, Monitor, Box, Eye, Zap, Send, Loader2,
  ChevronDown, ChevronRight, Edit3, User, Clock, Bot, Settings, Upload, Activity
} from 'lucide-react';
import AssetAuditTrailModal from '@/components/AssetAuditTrailModal';

const ICON_MAP = {
  'file-text': FileText, 'users': UsersIcon, 'image': ImageIcon, 'mic': Mic,
  'music': Music, 'palette': Palette, 'film': Film, 'layers': Layers,
  'monitor': Monitor, 'box': Box, 'eye': Eye, 'zap': Zap, 'layout': Layers, 'file': FileText,
};

export default function MasterLibraryPage({ projectId, projectName, api }) {
  const [categories, setCategories] = useState([]);
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState(null);
  const [activeCategory, setActiveCategory] = useState(null);
  const [search, setSearch] = useState('');
  const [showAdd, setShowAdd] = useState(false);
  const [showAI, setShowAI] = useState(false);
  const [showAddCategory, setShowAddCategory] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [freelancers, setFreelancers] = useState([]);
  const [aiText, setAiText] = useState('');
  const [aiLoading, setAiLoading] = useState(false);
  const [aiResult, setAiResult] = useState(null);
  const [newCat, setNewCat] = useState({ key: '', label: '', color: '#71717a' });
  const [form, setForm] = useState({ name: '', description: '', category: '', assigned_to: '', priority: 3, due_date: '', notes: '', details: {} });
  const [auditAssetId, setAuditAssetId] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [parsingFile, setParsingFile] = useState(null);
  const fileInputRef = useRef(null);

  const loadData = useCallback(async () => {
    const [c, s, f] = await Promise.all([
      api.get(`/projects/${projectId}/categories`),
      api.get(`/projects/${projectId}/master-library/summary`),
      api.get('/resources/availability').catch(() => ({ data: [] })),
    ]);
    setCategories(c.data);
    setSummary(s.data);
    setFreelancers(f.data);
    if (!activeCategory && c.data.length > 0) setActiveCategory(c.data[0].key);
  }, [api, projectId, activeCategory]);

  useEffect(() => { loadData(); }, [loadData]);

  useEffect(() => {
    if (!activeCategory) return;
    api.get(`/projects/${projectId}/master-library?category=${activeCategory}`).then(r => setItems(r.data));
  }, [activeCategory, api, projectId]);

  const reload = async () => {
    const [s, i] = await Promise.all([
      api.get(`/projects/${projectId}/master-library/summary`),
      activeCategory ? api.get(`/projects/${projectId}/master-library?category=${activeCategory}`) : Promise.resolve({ data: [] }),
    ]);
    setSummary(s.data);
    setItems(i.data);
  };

  const addItem = async () => {
    await api.post(`/projects/${projectId}/master-library`, { ...form, category: activeCategory });
    setShowAdd(false);
    setForm({ name: '', description: '', category: '', assigned_to: '', priority: 3, due_date: '', notes: '', details: {} });
    reload();
  };

  const uploadFile = async (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    setUploading(true);
    const isScreenplay = activeCategory === 'screenplay';
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      const formData = new FormData();
      formData.append('file', file);
      formData.append('category', activeCategory);
      formData.append('name', file.name);
      formData.append('description', '');
      try {
        await api.post(`/projects/${projectId}/master-library/upload`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      } catch (err) { console.error('Upload error:', err); }
    }
    setUploading(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
    reload();

    // Auto-parse if screenplay uploaded
    if (isScreenplay) {
      setAiLoading(true);
      setAiResult(null);
      try {
        const res = await api.post(`/projects/${projectId}/ai/parse-screenplay`, { text: '' });
        setAiResult(res.data);
        reload();
      } catch (err) {
        setAiResult({ status: 'error', message: err.response?.data?.detail || 'AI parsing failed. Check uploaded files.' });
      }
      setAiLoading(false);
    }
  };

  const parseFile = async (itemId, itemName) => {
    setParsingFile(itemId);
    setAiResult({ status: 'processing', message: `AI is parsing "${itemName}". This takes 1-2 minutes...` });
    try {
      await api.post(`/projects/${projectId}/ai/parse-single-file/${itemId}`);
      // Poll for result
      let attempts = 0;
      const poll = setInterval(async () => {
        attempts++;
        try {
          const status = await api.get(`/projects/${projectId}/parse-status/${itemId}`);
          if (status.data.status === 'parsed') {
            clearInterval(poll);
            setAiResult({ status: 'success', items_created: status.data.parsed_items, message: `Populated ${status.data.parsed_items} items from ${itemName}` });
            setParsingFile(null);
            reload();
          } else if (status.data.status === 'error') {
            clearInterval(poll);
            setAiResult({ status: 'error', message: `Parse error: ${status.data.result?.error || 'Unknown'}` });
            setParsingFile(null);
          } else if (attempts > 40) { // 40 * 3s = 2 minutes max
            clearInterval(poll);
            setAiResult({ status: 'success', message: 'Parsing may still be running. Refresh the page in a moment.' });
            setParsingFile(null);
            reload();
          }
        } catch (e) { /* keep polling */ }
      }, 3000);
    } catch (err) {
      setAiResult({ status: 'error', message: err.response?.data?.detail || `Failed to start parsing ${itemName}` });
      setParsingFile(null);
    }
  };

  const updateItem = async (id, updates) => {
    await api.put(`/projects/${projectId}/master-library/${id}`, updates);
    setEditingItem(null);
    reload();
  };

  const deleteItem = async (id) => {
    await api.delete(`/projects/${projectId}/master-library/${id}`);
    reload();
  };

  const addCustomCategory = async () => {
    await api.post(`/projects/${projectId}/categories`, newCat);
    const c = await api.get(`/projects/${projectId}/categories`);
    setCategories(c.data);
    setShowAddCategory(false);
    setNewCat({ key: '', label: '', color: '#71717a' });
  };

  const parseScreenplay = async () => {
    setAiLoading(true);
    try {
      // Send text if provided, otherwise AI will read uploaded docx files
      const res = await api.post(`/projects/${projectId}/ai/parse-screenplay`, { text: aiText || '' });
      setAiResult(res.data);
      reload();
    } catch (e) { console.error(e); }
    setAiLoading(false);
  };

  const parseFromUploads = async () => {
    setAiLoading(true);
    try {
      // Empty text = AI reads from uploaded screenplay docx files
      const res = await api.post(`/projects/${projectId}/ai/parse-screenplay`, { text: '' });
      setAiResult(res.data);
      setShowAI(true);
      reload();
    } catch (e) {
      alert(e.response?.data?.detail || 'Error parsing screenplay');
      console.error(e);
    }
    setAiLoading(false);
  };

  const [selectedItem, setSelectedItem] = useState(null);
  const [itemDetail, setItemDetail] = useState(null);
  const [showNASPicker, setShowNASPicker] = useState(false);
  const [nasFiles, setNasFiles] = useState([]);
  const [nasPath, setNasPath] = useState('');
  const [linkLabel, setLinkLabel] = useState('');

  const openItemDetail = async (item) => {
    setSelectedItem(item);
    try {
      const res = await api.get(`/library-item/${item.id}/detail`);
      setItemDetail(res.data);
    } catch (e) { setItemDetail(item); }
  };

  const browseNASForItem = async (path = '') => {
    setNasPath(path);
    setNasFiles([]);
    // Try up to 3 times with increasing timeout
    for (let attempt = 0; attempt < 3; attempt++) {
      try {
        const res = await api.post('/nas/bridge/send-command', { action: 'list_dir', path });
        if (res.data?.files) {
          setNasFiles(res.data.files);
          return;
        }
        if (res.data?.status === 'timeout') {
          // Wait and retry
          await new Promise(r => setTimeout(r, 2000));
          continue;
        }
        setNasFiles(res.data?.files || []);
        return;
      } catch (e) {
        if (attempt < 2) await new Promise(r => setTimeout(r, 2000));
      }
    }
  };

  const linkNASToItem = async (filePath) => {
    await api.post('/nas/link-file-to-library', { item_id: selectedItem.id, nas_path: filePath, label: linkLabel || filePath.split('/').pop() });
    setLinkLabel('');
    // Refresh detail
    const res = await api.get(`/library-item/${selectedItem.id}/detail`);
    setItemDetail(res.data);
  };

  const unlinkNAS = async (attachmentId) => {
    await api.delete(`/nas/unlink-file/${selectedItem.id}/${attachmentId}`);
    const res = await api.get(`/library-item/${selectedItem.id}/detail`);
    setItemDetail(res.data);
  };

  const activeMeta = categories.find(c => c.key === activeCategory) || {};
  const ActiveIcon = ICON_MAP[activeMeta.icon] || FileText;
  const [crawling, setCrawling] = useState(false);
  const [crawlResult, setCrawlResult] = useState(null);
  const [shotRegistry, setShotRegistry] = useState(null);
  const [discoveringShots, setDiscoveringShots] = useState(false);
  const [nasBrowsePath, setNasBrowsePath] = useState('');
  const [nasBrowseFiles, setNasBrowseFiles] = useState([]);
  const [showNasBrowse, setShowNasBrowse] = useState(false);
  const [browsingNas, setBrowsingNas] = useState(false);

  const autoCrawlAndLink = async () => {
    setCrawling(true);
    setCrawlResult(null);
    try {
      const res = await api.post(`/projects/${projectId}/crawl-nas-and-link`);
      setCrawlResult(res.data);
      reload();
    } catch (e) {
      setCrawlResult({ status: 'error', message: e.response?.data?.detail || 'Crawl failed' });
    }
    setCrawling(false);
  };

  const catCounts = summary?.categories || {};

  // Load shot registry when CBA tab is active
  useEffect(() => {
    if (activeCategory === 'cut_by_animatics') {
      api.get(`/shots/registry/${projectId}`).then(r => setShotRegistry(r.data)).catch(() => {});
    }
  }, [activeCategory, api, projectId]);

  const discoverShotsFromNAS = async (cbaPath) => {
    setDiscoveringShots(true);
    setCrawlResult(null);
    try {
      // Use the project's mapped NAS folder as the discovery root.
      // Falls back to "" (share root) so users see the full tree if not mapped yet.
      let nasRoot = '';
      try {
        const mapRes = await api.get(`/nas/project-mapping/${projectId}`);
        nasRoot = mapRes.data?.nas_folder || '';
      } catch (_e) { /* no mapping yet */ }
      const payload = { nas_path: nasRoot };
      if (cbaPath) payload.cba_path = cbaPath;
      const res = await api.post(`/shots/discover/${projectId}`, payload);
      if (res.data.error) {
        setCrawlResult({ status: 'error', message: res.data.error });
        setDiscoveringShots(false);
      } else {
        setCrawlResult({ status: 'started', message: res.data.message || 'Discovery running in background...' });
        // Poll for completion
        const poll = setInterval(async () => {
          try {
            const st = await api.get(`/shots/discover-status/${projectId}`);
            if (st.data.status === 'done') {
              clearInterval(poll);
              setCrawlResult({ status: 'success', message: `Discovered ${st.data.result?.total || st.data.total_shots} shots!` });
              api.get(`/shots/registry/${projectId}`).then(r => setShotRegistry(r.data));
              setDiscoveringShots(false);
            } else if (st.data.status === 'error') {
              clearInterval(poll);
              setCrawlResult({ status: 'error', message: st.data.result?.error || 'Discovery failed' });
              setDiscoveringShots(false);
            }
          } catch (e) {}
        }, 3000);
        // Timeout after 2 minutes
        setTimeout(() => { clearInterval(poll); setDiscoveringShots(false); api.get(`/shots/registry/${projectId}`).then(r => setShotRegistry(r.data)); }, 120000);
      }
    } catch (e) {
      setCrawlResult({ status: 'error', message: e.response?.data?.detail || 'Discovery failed' });
      setDiscoveringShots(false);
    }
  };

  const browseNAS = async (path) => {
    setBrowsingNas(true);
    setNasBrowsePath(path);
    setNasBrowseFiles([]);
    try {
      const res = await api.post('/nas/bridge/send-command', { action: 'list_dir', path });
      // Handle both direct response and nested response
      const files = res.data?.files || res.data?.result?.files || [];
      setNasBrowseFiles(files);
    } catch (e) {
      console.error('Browse NAS error:', e);
      setNasBrowseFiles([]);
    }
    setBrowsingNas(false);
  };

  const filteredItems = items.filter(i => !search || i.name?.toLowerCase().includes(search.toLowerCase()) || i.description?.toLowerCase().includes(search.toLowerCase()));

  return (
    <div className="space-y-4" data-testid="master-library">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-heading font-bold text-white">Master Data Library</h2>
          <p className="text-[10px] text-zinc-500">{projectName} | {summary?.total_items || 0} total items across all categories</p>
        </div>
        <div className="flex gap-2">
          <button data-testid="auto-crawl-btn" onClick={autoCrawlAndLink} disabled={crawling}
            className="h-7 px-3 bg-amber-600 hover:bg-amber-500 text-white text-[10px] font-medium rounded-sm flex items-center gap-1.5 disabled:opacity-50">
            {crawling ? <Loader2 className="w-3 h-3 animate-spin" /> : <Search className="w-3 h-3" />}
            {crawling ? 'Crawling NAS...' : 'Auto-Link NAS Assets'}
          </button>
          <button onClick={() => setShowAddCategory(true)} className="h-7 px-3 bg-zinc-700 hover:bg-zinc-600 text-zinc-300 text-[10px] rounded-sm flex items-center gap-1.5">
            <Settings className="w-3 h-3" /> Add Category
          </button>
        </div>
      </div>

      {/* AI Parse Status Banner */}
      {aiLoading && (
        <div className="bg-violet-500/10 border border-violet-500/20 rounded-sm px-4 py-3 flex items-center gap-3">
          <Loader2 className="w-4 h-4 text-violet-400 animate-spin" />
          <p className="text-xs text-violet-300">AI is reading screenplay files and extracting Characters, BGs, Props, Voice Overs...</p>
        </div>
      )}
      {aiResult && !aiLoading && (
        <div className={`${aiResult.status === 'success' ? 'bg-emerald-500/10 border-emerald-500/20' : aiResult.status === 'error' ? 'bg-red-500/10 border-red-500/20' : 'bg-amber-500/10 border-amber-500/20'} border rounded-sm px-4 py-3`}>
          <div className="flex items-center justify-between">
            {aiResult.status === 'success' ? (
              <div>
                <p className="text-xs text-emerald-400 font-medium">AI populated {aiResult.items_created} items with {aiResult.chapter_links_created} chapter links</p>
                <div className="flex flex-wrap gap-2 mt-1">
                  {Object.entries(aiResult.breakdown || {}).filter(([k]) => k !== 'chapter_links').map(([k, v]) => (
                    v > 0 && <span key={k} className="text-[10px] px-2 py-0.5 bg-zinc-800 rounded text-zinc-300">{k}: <span className="text-emerald-400 font-mono">{v}</span></span>
                  ))}
                </div>
              </div>
            ) : (
              <p className="text-xs text-amber-400">{aiResult.message || 'AI response received — check library tabs for extracted data'}</p>
            )}
            <button onClick={() => setAiResult(null)} className="text-zinc-600 hover:text-white text-[10px]">Dismiss</button>
          </div>
        </div>
      )}

      {/* Crawl Result */}
      {crawlResult && (
        <div className={`${crawlResult.status === 'success' ? 'bg-amber-500/10 border-amber-500/20' : 'bg-red-500/10 border-red-500/20'} border rounded-sm px-4 py-3`}>
          <div className="flex items-center justify-between">
            {crawlResult.status === 'success' ? (
              <div>
                <p className="text-xs text-amber-400 font-medium">Auto-linked {crawlResult.matched} items | {crawlResult.unmatched_count} unmatched (link manually)</p>
                {crawlResult.match_results?.slice(0, 5).map((m, i) => (
                  <p key={i} className="text-[9px] text-zinc-500 mt-0.5">{m.item} → {m.matched_to} ({m.score}% match)</p>
                ))}
              </div>
            ) : (
              <p className="text-xs text-red-400">{crawlResult.message || 'Crawl failed'}</p>
            )}
            <button onClick={() => setCrawlResult(null)} className="text-zinc-600 hover:text-white text-[10px]">Dismiss</button>
          </div>
        </div>
      )}

      {/* Category Grid */}
      <div className="flex flex-wrap gap-1.5">
        {categories.map(cat => {
          const Icon = ICON_MAP[cat.icon] || FileText;
          const count = catCounts[cat.key]?.total || 0;
          const isActive = activeCategory === cat.key;
          return (
            <button key={cat.key} data-testid={`lib-cat-${cat.key}`} onClick={() => setActiveCategory(cat.key)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-sm text-[10px] font-medium transition-all ${isActive ? 'bg-zinc-800 text-white border border-zinc-700' : 'bg-zinc-900/50 text-zinc-500 border border-zinc-800/50 hover:border-zinc-700 hover:text-zinc-300'}`}>
              <Icon className="w-3 h-3" style={{ color: cat.color }} />
              {cat.label}
              {count > 0 && <span className="ml-0.5 text-[9px] font-mono" style={{ color: cat.color }}>{count}</span>}
            </button>
          );
        })}
      </div>

      {/* Active Category Content */}
      {activeCategory === 'cut_by_animatics' ? (
        /* ═══ CUT BY ANIMATICS — SPECIAL VIEW ═══ */
        <div className="bg-zinc-900 border border-zinc-800 rounded-sm">
          <div className="px-4 py-3 border-b border-zinc-800 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Film className="w-4 h-4 text-cyan-400" />
              <h3 className="text-xs font-semibold text-zinc-300 uppercase tracking-wider">Cut By Animatics — Shot Discovery</h3>
            </div>
            <div className="flex gap-2">
              <button onClick={() => discoverShotsFromNAS()} disabled={discoveringShots}
                className="h-7 px-3 bg-cyan-600 hover:bg-cyan-500 text-white text-[10px] font-medium rounded-sm flex items-center gap-1.5 disabled:opacity-50">
                {discoveringShots ? <Loader2 className="w-3 h-3 animate-spin" /> : <Search className="w-3 h-3" />}
                {discoveringShots ? 'Scanning NAS...' : 'Fetch from NAS'}
              </button>
              <button onClick={() => { setShowNasBrowse(true); browseNAS(''); }}
                className="h-7 px-3 bg-amber-600 hover:bg-amber-500 text-white text-[10px] font-medium rounded-sm flex items-center gap-1.5">
                <Layers className="w-3 h-3" /> Browse NAS
              </button>
            </div>
          </div>

          {/* Direct Path Input */}
          <div className="px-4 py-3 border-b border-zinc-800/50 bg-zinc-800/10">
            <p className="text-[9px] text-zinc-500 uppercase mb-1.5">Direct NAS Path (paste the Cut Animatics folder path — e.g. <span className="text-zinc-400 font-mono">Fairy_Tales/Hansel_Gretel/01_Pre_Production/004_Cut_Animatics</span>)</p>
            <div className="flex gap-2">
              <input id="cba-direct-path"
                className="flex-1 h-8 bg-zinc-950 border border-zinc-800 rounded-sm px-3 text-xs text-white font-mono focus:ring-1 focus:ring-cyan-500 focus:outline-none"
                placeholder="e.g. Fairy_Tales/Hansel_Gretel/01_Pre_Production/004_Cut_Animatics" />
              <button onClick={() => { const p = document.getElementById('cba-direct-path').value; if (p) discoverShotsFromNAS(p); }}
                disabled={discoveringShots}
                className="h-8 px-4 bg-emerald-600 hover:bg-emerald-500 text-white text-[10px] font-medium rounded-sm disabled:opacity-50">
                Connect & Fetch
              </button>
            </div>
          </div>

          {/* Shot Summary */}
          {shotRegistry && shotRegistry.summary.total_shots > 0 && (
            <div className="px-4 py-2 border-b border-zinc-800/50 flex gap-4 text-[10px]">
              <span className="text-white font-mono">{shotRegistry.summary.total_shots} shots</span>
              <span className="text-emerald-400">{shotRegistry.summary.ready_shots} ready</span>
              {shotRegistry.summary.missing_audio > 0 && <span className="text-amber-400">{shotRegistry.summary.missing_audio} no audio</span>}
              {shotRegistry.summary.missing_video > 0 && <span className="text-red-400">{shotRegistry.summary.missing_video} no video</span>}
              <span className="text-blue-400">{shotRegistry.summary.animation_done} anim done</span>
            </div>
          )}

          {/* Shot List by Chapter */}
          {shotRegistry && shotRegistry.chapters?.length > 0 ? (
            <div className="divide-y divide-zinc-800/30 max-h-[50vh] overflow-y-auto">
              {shotRegistry.chapters.map(ch => (
                <div key={ch.chapter_number}>
                  <div className="px-4 py-2 bg-zinc-800/20 flex items-center gap-2">
                    <Layers className="w-3 h-3 text-cyan-400" />
                    <span className="text-[10px] text-zinc-300 font-medium">{ch.chapter_name}</span>
                    <span className="text-[9px] font-mono text-zinc-600">{ch.shots.length} shots</span>
                  </div>
                  {ch.shots.map(shot => (
                    <div key={shot.id} className="px-4 py-2 pl-8 flex items-center gap-3 hover:bg-zinc-800/20">
                      <span className="text-[11px] font-mono text-zinc-200 w-20">{shot.shot_name}</span>
                      <span className={`text-[10px] ${shot.has_video ? 'text-emerald-400' : 'text-red-400'}`} title="Video">{shot.has_video ? '🎬 ✓' : '🎬 ✗'}</span>
                      <span className={`text-[10px] ${shot.has_audio ? 'text-emerald-400' : 'text-red-400'}`} title="Audio">{shot.has_audio ? '🔊 ✓' : '🔊 ✗'}</span>
                      <span className="text-[9px] px-1.5 py-0.5 rounded-full" style={{
                        backgroundColor: shot.ingest_status === 'ready' ? '#10b98120' : '#f59e0b20',
                        color: shot.ingest_status === 'ready' ? '#10b981' : '#f59e0b'
                      }}>{shot.ingest_status === 'ready' ? 'READY' : shot.ingest_status?.replace('_', ' ').toUpperCase()}</span>
                      <span className="text-[9px] font-mono text-zinc-600 flex-1 truncate">{shot.nas_path}</span>
                      {shot.frames > 0 && <span className="text-[9px] font-mono text-zinc-500">{shot.frames}f</span>}
                    </div>
                  ))}
                </div>
              ))}
            </div>
          ) : (
            <div className="px-4 py-12 text-center">
              <Film className="w-10 h-10 text-zinc-800 mx-auto mb-3" />
              <p className="text-sm text-zinc-400">No shots discovered yet</p>
              <p className="text-xs text-zinc-600 mt-1">Click "Fetch from NAS" to scan CUT_BY_ANIMATICS folders in each chapter</p>
              <p className="text-[10px] text-zinc-700 mt-1">Or click "Browse NAS" to manually navigate to the folder</p>
            </div>
          )}
        </div>
      ) : (

      /* ═══ REGULAR CATEGORY CONTENT ═══ */
      <div className="bg-zinc-900 border border-zinc-800 rounded-sm">
        <div className="px-4 py-3 border-b border-zinc-800 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <ActiveIcon className="w-4 h-4" style={{ color: activeMeta.color }} />
            <h3 className="text-xs font-semibold text-zinc-300 uppercase tracking-wider">{activeMeta.label} ({filteredItems.length})</h3>
          </div>
          <div className="flex items-center gap-2">
            <div className="relative">
              <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3 text-zinc-600" />
              <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search..." className="h-7 pl-7 pr-2 w-40 bg-zinc-950 border border-zinc-800 rounded-sm text-[10px] text-white focus:outline-none focus:ring-1 focus:ring-blue-500" />
            </div>
            <button data-testid="upload-file-btn" onClick={() => fileInputRef.current?.click()} disabled={uploading}
              className="h-7 px-3 bg-emerald-600 hover:bg-emerald-500 text-white text-[10px] font-medium rounded-sm flex items-center gap-1.5 disabled:opacity-50">
              <Upload className="w-3 h-3" /> {uploading ? 'Uploading...' : 'Upload Files'}
            </button>
            <input ref={fileInputRef} type="file" multiple onChange={uploadFile} className="hidden" accept="*/*" />
            <button data-testid="add-library-item-btn" onClick={() => setShowAdd(true)} className="h-7 px-3 bg-blue-600 hover:bg-blue-500 text-white text-[10px] font-medium rounded-sm flex items-center gap-1.5">
              <Plus className="w-3 h-3" /> Add
            </button>
          </div>
        </div>

        <div className="divide-y divide-zinc-800/30 max-h-[50vh] overflow-y-auto">
          {filteredItems.map(item => (
            <div key={item.id} className="px-4 py-3 hover:bg-zinc-800/20 transition-colors cursor-pointer group" data-testid={`lib-item-${item.id}`}
              onClick={() => openItemDetail(item)}>
              <div className="flex items-start justify-between">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="text-xs text-zinc-200 font-medium group-hover:text-blue-400 transition-colors">{item.name}</p>
                    <span className={`status-${item.status} px-1.5 py-0.5 rounded-full text-[8px] font-medium`}>{item.status?.replace('_', ' ')}</span>
                    <span className="text-[9px] font-mono text-zinc-700">v{item.version}</span>
                    {(item.nas_attachments?.length > 0) && <span className="text-[9px] text-emerald-500">{item.nas_attachments.length} files linked</span>}
                  </div>
                  {item.description && <p className="text-[10px] text-zinc-600 mt-0.5 line-clamp-2">{item.description}</p>}
                  <div className="flex items-center gap-3 mt-1">
                    {item.assignee_name && <span className="text-[10px] text-zinc-500 flex items-center gap-1"><User className="w-3 h-3" />{item.assignee_name}</span>}
                    {item.due_date && <span className="text-[10px] text-zinc-600 flex items-center gap-1"><Clock className="w-3 h-3" />{item.due_date}</span>}
                  </div>
                </div>
                <div className="flex gap-1 ml-3 flex-shrink-0" onClick={e => e.stopPropagation()}>
                  {(activeCategory === 'screenplay' || item.file_type === '.docx' || item.file_type === '.pdf' || item.file_type === '.txt') && item.status !== 'parsed' && (
                    <button data-testid={`populate-${item.id}`} onClick={() => parseFile(item.id, item.name)} disabled={parsingFile === item.id}
                      className="h-6 px-2 bg-emerald-500/20 text-emerald-400 text-[9px] rounded-sm hover:bg-emerald-500/30 flex items-center gap-1 disabled:opacity-50">
                      {parsingFile === item.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Bot className="w-3 h-3" />}
                      {parsingFile === item.id ? 'Parsing...' : 'Populate'}
                    </button>
                  )}
                  {item.status === 'parsed' && (
                    <span className="h-6 px-2 bg-emerald-500/10 text-emerald-500 text-[9px] rounded-sm flex items-center gap-1">
                      Populated {item.details?.parsed_items || ''}
                    </span>
                  )}
                  <button onClick={() => setEditingItem(item)} className="h-6 px-2 bg-zinc-800 text-zinc-400 text-[9px] rounded-sm hover:bg-zinc-700"><Edit3 className="w-3 h-3" /></button>
                  <button onClick={(e) => { e.stopPropagation(); setAuditAssetId(item.id); }}
                          data-testid={`audit-${item.id}`}
                          title="View asset journey (every stage, decision, comment)"
                          className="h-6 px-2 bg-violet-500/15 hover:bg-violet-500/25 text-violet-300 text-[9px] rounded-sm flex items-center gap-1"><Activity className="w-3 h-3" /> Journey</button>
                  <button onClick={() => deleteItem(item.id)} className="h-6 px-2 bg-red-500/10 text-red-400 text-[9px] rounded-sm hover:bg-red-500/20"><Trash2 className="w-3 h-3" /></button>
                </div>
              </div>
            </div>
          ))}
          {filteredItems.length === 0 && (
            <div className="px-4 py-10 text-center">
              <ActiveIcon className="w-8 h-8 text-zinc-800 mx-auto mb-2" />
              <p className="text-xs text-zinc-600">No {activeMeta.label?.toLowerCase()} in master library yet</p>
              <p className="text-[10px] text-zinc-700 mt-1">Add items here, then link them to chapters</p>
            </div>
          )}
        </div>
      </div>
      )} {/* end regular category ternary */}

      {/* Add Item Modal */}
      {showAdd && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50" data-testid="add-library-modal">
          <div className="bg-zinc-900 border border-zinc-800 rounded-sm p-6 w-[32rem]">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-heading font-semibold text-white">Add to {activeMeta.label}</h3>
              <button onClick={() => setShowAdd(false)}><X className="w-4 h-4 text-zinc-600" /></button>
            </div>
            <div className="space-y-3">
              <div><label className="text-[10px] text-zinc-500 uppercase">Name *</label>
                <input data-testid="lib-item-name" value={form.name} onChange={e => setForm({...form, name: e.target.value})} className="w-full h-9 bg-zinc-950 border border-zinc-800 rounded-sm px-3 text-sm text-white focus:ring-1 focus:ring-blue-500 focus:outline-none" placeholder={`${activeMeta.label} name`} /></div>
              <div><label className="text-[10px] text-zinc-500 uppercase">Description</label>
                <textarea value={form.description} onChange={e => setForm({...form, description: e.target.value})} rows={2} className="w-full bg-zinc-950 border border-zinc-800 rounded-sm px-3 py-2 text-xs text-white focus:outline-none" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><label className="text-[10px] text-zinc-500 uppercase">Assign To</label>
                  <select value={form.assigned_to} onChange={e => setForm({...form, assigned_to: e.target.value})} className="w-full h-8 bg-zinc-950 border border-zinc-800 rounded-sm px-2 text-xs text-zinc-300">
                    <option value="">Unassigned</option>
                    {freelancers.map(f => <option key={f.id} value={f.id}>{f.name}</option>)}
                  </select></div>
                <div><label className="text-[10px] text-zinc-500 uppercase">Due Date</label>
                  <input type="date" value={form.due_date} onChange={e => setForm({...form, due_date: e.target.value})} className="w-full h-8 bg-zinc-950 border border-zinc-800 rounded-sm px-2 text-xs text-zinc-300" /></div>
              </div>
              <div><label className="text-[10px] text-zinc-500 uppercase">Notes</label>
                <textarea value={form.notes} onChange={e => setForm({...form, notes: e.target.value})} rows={2} className="w-full bg-zinc-950 border border-zinc-800 rounded-sm px-3 py-2 text-xs text-zinc-400 focus:outline-none" placeholder="Specs, file paths, references..." /></div>
            </div>
            <div className="flex gap-2 mt-4">
              <button data-testid="lib-item-save" onClick={addItem} disabled={!form.name} className="flex-1 h-9 bg-blue-600 hover:bg-blue-500 text-white text-xs rounded-sm disabled:opacity-40">Add to Library</button>
              <button onClick={() => setShowAdd(false)} className="w-24 h-9 bg-zinc-800 text-zinc-400 text-xs rounded-sm">Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Item Modal */}
      {editingItem && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50">
          <div className="bg-zinc-900 border border-zinc-800 rounded-sm p-6 w-[32rem]">
            <h3 className="text-sm font-heading font-semibold text-white mb-4">Edit: {editingItem.name}</h3>
            <div className="space-y-3">
              <div><label className="text-[10px] text-zinc-500 uppercase">Name</label>
                <input value={editingItem.name} onChange={e => setEditingItem({...editingItem, name: e.target.value})} className="w-full h-8 bg-zinc-950 border border-zinc-800 rounded-sm px-3 text-xs text-white" /></div>
              <div><label className="text-[10px] text-zinc-500 uppercase">Description</label>
                <textarea value={editingItem.description || ''} onChange={e => setEditingItem({...editingItem, description: e.target.value})} rows={2} className="w-full bg-zinc-950 border border-zinc-800 rounded-sm px-3 py-2 text-xs text-white" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><label className="text-[10px] text-zinc-500 uppercase">Status</label>
                  <select value={editingItem.status} onChange={e => setEditingItem({...editingItem, status: e.target.value})} className="w-full h-8 bg-zinc-950 border border-zinc-800 rounded-sm px-2 text-xs text-zinc-300">
                    {['draft', 'in_progress', 'review', 'approved', 'completed'].map(s => <option key={s} value={s}>{s.replace('_',' ')}</option>)}
                  </select></div>
                <div><label className="text-[10px] text-zinc-500 uppercase">Assign To</label>
                  <select value={editingItem.assigned_to || ''} onChange={e => setEditingItem({...editingItem, assigned_to: e.target.value})} className="w-full h-8 bg-zinc-950 border border-zinc-800 rounded-sm px-2 text-xs text-zinc-300">
                    <option value="">Unassigned</option>
                    {freelancers.map(f => <option key={f.id} value={f.id}>{f.name}</option>)}
                  </select></div>
              </div>
            </div>
            <div className="flex gap-2 mt-4">
              <button onClick={() => updateItem(editingItem.id, { name: editingItem.name, description: editingItem.description, status: editingItem.status, assigned_to: editingItem.assigned_to })} className="flex-1 h-8 bg-blue-600 text-white text-xs rounded-sm">Save</button>
              <button onClick={() => setEditingItem(null)} className="flex-1 h-8 bg-zinc-800 text-zinc-400 text-xs rounded-sm">Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Add Custom Category Modal */}
      {showAddCategory && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50">
          <div className="bg-zinc-900 border border-zinc-800 rounded-sm p-6 w-80">
            <h3 className="text-sm font-heading font-semibold text-white mb-4">Add Custom Category</h3>
            <div className="space-y-2.5">
              <div><label className="text-[10px] text-zinc-500 uppercase">Label</label>
                <input value={newCat.label} onChange={e => setNewCat({...newCat, label: e.target.value, key: e.target.value.toLowerCase().replace(/\s+/g, '_')})} className="w-full h-8 bg-zinc-950 border border-zinc-800 rounded-sm px-3 text-xs text-white" placeholder="e.g., Motion Capture" /></div>
              <div><label className="text-[10px] text-zinc-500 uppercase">Color</label>
                <input type="color" value={newCat.color} onChange={e => setNewCat({...newCat, color: e.target.value})} className="w-full h-8 bg-zinc-950 border border-zinc-800 rounded-sm" /></div>
            </div>
            <div className="flex gap-2 mt-4">
              <button onClick={addCustomCategory} disabled={!newCat.label} className="flex-1 h-8 bg-blue-600 text-white text-xs rounded-sm disabled:opacity-40">Create</button>
              <button onClick={() => setShowAddCategory(false)} className="flex-1 h-8 bg-zinc-800 text-zinc-400 text-xs rounded-sm">Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* NAS Browse Modal for CBA */}
      {showNasBrowse && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50">
          <div className="bg-zinc-900 border border-zinc-800 rounded-sm w-[36rem] max-h-[70vh] flex flex-col">
            <div className="px-4 py-3 border-b border-zinc-800 flex items-center justify-between">
              <h4 className="text-sm font-heading font-bold text-white">Browse NAS — Find CUT_BY_ANIMATICS</h4>
              <button onClick={() => setShowNasBrowse(false)}><X className="w-4 h-4 text-zinc-600" /></button>
            </div>
            <div className="px-4 py-2 border-b border-zinc-800/50 flex items-center gap-2 text-[10px] text-zinc-500">
              <button onClick={() => browseNAS('')} className="hover:text-white">Root</button>
              {nasBrowsePath.split('/').filter(Boolean).map((part, i, arr) => (
                <span key={i} className="flex items-center gap-1">
                  <span className="text-zinc-700">/</span>
                  <button onClick={() => browseNAS(arr.slice(0, i+1).join('/'))} className="hover:text-white">{part}</button>
                </span>
              ))}
            </div>
            <div className="flex-1 overflow-y-auto divide-y divide-zinc-800/30">
              {nasBrowsePath && (
                <button onClick={() => { const p = nasBrowsePath.split('/').filter(Boolean); p.pop(); browseNAS(p.join('/')); }}
                  className="w-full text-left px-4 py-2 text-xs text-zinc-500 hover:bg-zinc-800/30">..</button>
              )}
              {browsingNas ? (
                <p className="text-xs text-zinc-600 text-center py-6">Scanning NAS... (bridge agent must be running)</p>
              ) : nasBrowseFiles.length > 0 ? nasBrowseFiles.map((f, i) => {
                const isCBA = f.is_dir && f.name.toLowerCase().replace(/[\s_-]/g, '').includes('cutbyanimatics');
                return (
                <div key={i} className="px-4 py-2 flex items-center justify-between hover:bg-zinc-800/20">
                  {f.is_dir ? (
                    <>
                      <button onClick={() => browseNAS(f.path)} className={`flex-1 text-left text-xs font-medium ${isCBA ? 'text-cyan-400' : 'text-amber-400'} hover:text-white`}>
                        {f.name}/ {isCBA && <span className="text-[8px] bg-cyan-500/20 text-cyan-400 px-1.5 py-0.5 rounded ml-2">CUT BY ANIMATICS</span>}
                      </button>
                      <button onClick={() => { discoverShotsFromNAS(f.path); setShowNasBrowse(false); }}
                        className={`h-6 px-3 ${isCBA ? 'bg-cyan-600 text-white' : 'bg-zinc-700 text-zinc-300'} text-[9px] rounded-sm hover:opacity-80`}>
                        {isCBA ? 'Connect & Fetch' : 'Use This'}
                      </button>
                    </>
                  ) : (
                    <span className="text-xs text-zinc-500">{f.name} <span className="text-[9px] text-zinc-700">{f.size_human}</span></span>
                  )}
                </div>
                );
              }) : (
                <p className="text-xs text-zinc-600 text-center py-6">No files found. Make sure NAS bridge is running on your QNAP.</p>
              )}
            </div>
            <div className="px-4 py-2 border-t border-zinc-800 text-[9px] text-zinc-600">
              Navigate to your chapter's CUT_BY_ANIMATICS folder and click "Use This Folder"
            </div>
          </div>
        </div>
      )}

      {/* Item Detail Panel — NAS File Attachments */}
      {selectedItem && itemDetail && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50" data-testid="item-detail-modal">
          <div className="bg-zinc-900 border border-zinc-800 rounded-sm w-[48rem] max-h-[80vh] flex flex-col">
            <div className="px-6 py-4 border-b border-zinc-800 flex items-center justify-between">
              <div>
                <h3 className="text-lg font-heading font-bold text-white">{itemDetail.name}</h3>
                <p className="text-[10px] text-zinc-500">{itemDetail.category} | {itemDetail.status} | v{itemDetail.version}</p>
              </div>
              <button onClick={() => { setSelectedItem(null); setItemDetail(null); setShowNASPicker(false); }} className="text-zinc-600 hover:text-white"><X className="w-5 h-5" /></button>
            </div>

            <div className="flex-1 overflow-y-auto p-6 space-y-4">
              {itemDetail.description && <p className="text-xs text-zinc-400">{itemDetail.description}</p>}

              {/* Chapter Links */}
              {itemDetail.chapter_links?.length > 0 && (
                <div>
                  <h4 className="text-[10px] text-zinc-500 uppercase tracking-wider mb-2">Linked to Chapters</h4>
                  <div className="flex flex-wrap gap-1.5">
                    {itemDetail.chapter_links.map(ch => (
                      <span key={ch.ref_id} className="text-[10px] px-2 py-1 bg-cyan-500/10 text-cyan-400 border border-cyan-500/20 rounded-sm">{ch.chapter_name}</span>
                    ))}
                  </div>
                </div>
              )}

              {/* NAS Attached Files */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <h4 className="text-[10px] text-zinc-500 uppercase tracking-wider">NAS Files ({itemDetail.nas_attachments?.length || 0})</h4>
                  <button data-testid="attach-nas-btn" onClick={() => { setShowNASPicker(true); browseNASForItem(''); }}
                    className="h-6 px-3 bg-emerald-600 hover:bg-emerald-500 text-white text-[9px] font-medium rounded-sm flex items-center gap-1">
                    <Upload className="w-3 h-3" /> Browse NAS
                  </button>
                </div>

                {/* Manual Path Input */}
                <div className="space-y-2 mb-3">
                  <div className="flex gap-2">
                    <input value={linkLabel} onChange={e => setLinkLabel(e.target.value)}
                      className="flex-1 h-7 bg-zinc-950 border border-zinc-800 rounded-sm px-2 text-[10px] text-white focus:outline-none focus:ring-1 focus:ring-blue-500"
                      placeholder="Type NAS path: e.g. Fairy_Tales/02_Library/Char/Human/Dimitry" />
                    <button onClick={async () => {
                      if (!linkLabel.trim()) return;
                      await api.post(`/projects/${selectedItem?.project_id || ''}/master-library/${selectedItem?.id}/link-nas-path`, { nas_path: linkLabel, label: linkLabel.split('/').pop() || linkLabel });
                      setLinkLabel('');
                      const res = await api.get(`/library-item/${selectedItem.id}/detail`);
                      setItemDetail(res.data);
                    }} disabled={!linkLabel.trim()}
                      className="h-7 px-3 bg-blue-600 hover:bg-blue-500 text-white text-[9px] rounded-sm disabled:opacity-40">Link</button>
                  </div>
                  <div className="flex gap-1 flex-wrap">
                    <span className="text-[9px] text-zinc-600">Quick add subfolder:</span>
                    {['Model', 'Texture', 'Rigged', 'Concept', 'Turntable', 'Reference'].map(sub => (
                      <button key={sub} onClick={() => setLinkLabel(prev => {
                        if (!prev || prev.length < 3) return `02_Library/${sub}`;
                        return prev.replace(/\/?$/, '/' + sub);
                      })}
                        className="text-[8px] px-1.5 py-0.5 bg-zinc-800 text-zinc-400 rounded hover:bg-zinc-700 hover:text-white">{sub}</button>
                    ))}
                  </div>
                </div>

                <div className="space-y-1.5">
                  {(itemDetail.nas_attachments || []).map(att => (
                    <div key={att.id} className="flex items-center gap-3 bg-zinc-800/30 border border-zinc-800/50 rounded-sm px-3 py-2.5 group">
                      <div className="w-8 h-8 bg-zinc-800 rounded-sm flex items-center justify-center flex-shrink-0">
                        {att.nas_path?.includes('Model') || att.nas_path?.includes('model') ? <Box className="w-4 h-4 text-blue-400" /> :
                         att.nas_path?.includes('Rig') || att.nas_path?.includes('rig') ? <Zap className="w-4 h-4 text-amber-400" /> :
                         att.nas_path?.includes('Texture') || att.nas_path?.includes('texture') ? <Palette className="w-4 h-4 text-emerald-400" /> :
                         att.nas_path?.endsWith('.blend') ? <FileText className="w-4 h-4 text-orange-400" /> :
                         <Layers className="w-4 h-4 text-zinc-500" />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-xs text-zinc-200 font-medium">{att.label}</p>
                        <p className="text-[9px] font-mono text-zinc-600 truncate">{att.nas_path}</p>
                        {att.auto_matched && <span className="text-[8px] text-emerald-500">auto-matched ({att.match_score}%)</span>}
                      </div>
                      <button onClick={() => unlinkNAS(att.id)} className="text-zinc-700 hover:text-red-400 opacity-0 group-hover:opacity-100 transition-opacity"><Trash2 className="w-3.5 h-3.5" /></button>
                    </div>
                  ))}
                  {(!itemDetail.nas_attachments || itemDetail.nas_attachments.length === 0) && (
                    <p className="text-[10px] text-zinc-700 text-center py-3">No NAS files attached. Type a path above or browse NAS.</p>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* NAS File Picker */}
      {showNASPicker && (
        <div className="fixed inset-0 bg-black/90 flex items-center justify-center z-[60]" data-testid="nas-picker-modal">
          <div className="bg-zinc-900 border border-zinc-800 rounded-sm w-[36rem] max-h-[70vh] flex flex-col">
            <div className="px-4 py-3 border-b border-zinc-800 flex items-center justify-between">
              <h4 className="text-sm font-heading font-bold text-white">Browse NAS — Select file to attach</h4>
              <button onClick={() => setShowNASPicker(false)} className="text-zinc-600 hover:text-white"><X className="w-4 h-4" /></button>
            </div>
            <div className="px-4 py-2 border-b border-zinc-800/50 flex items-center gap-2 text-[10px] text-zinc-500">
              <button onClick={() => browseNASForItem('')} className="hover:text-white">Root</button>
              {nasPath.split('/').filter(Boolean).map((part, i, arr) => (
                <span key={i} className="flex items-center gap-1">
                  <ChevronRight className="w-3 h-3 text-zinc-700" />
                  <button onClick={() => browseNASForItem(arr.slice(0, i + 1).join('/'))} className="hover:text-white">{part}</button>
                </span>
              ))}
            </div>
            <div className="flex-1 overflow-y-auto divide-y divide-zinc-800/30">
              {nasPath && (
                <>
                <button onClick={() => { const parts = nasPath.split('/').filter(Boolean); parts.pop(); browseNASForItem(parts.join('/')); }}
                  className="w-full text-left px-4 py-2 text-xs text-zinc-500 hover:bg-zinc-800/30">..</button>
                <button onClick={() => { linkNASToItem(nasPath); setShowNASPicker(false); }}
                  className="w-full text-left px-4 py-2.5 text-xs text-emerald-400 hover:bg-emerald-500/10 font-medium border-b border-zinc-800">
                  Link this folder: {nasPath.split('/').pop()}
                </button>
                </>
              )}
              {nasFiles.map((f, i) => (
                <div key={i} className="px-4 py-2 flex items-center justify-between hover:bg-zinc-800/20">
                  {f.is_dir ? (
                    <>
                    <button onClick={() => browseNASForItem(f.path)} className="flex-1 text-left text-xs text-amber-400 hover:text-amber-300 font-medium">{f.name}/</button>
                    <button onClick={() => { linkNASToItem(f.path); setShowNASPicker(false); }}
                      className="h-6 px-3 bg-emerald-600/20 text-emerald-400 text-[9px] rounded-sm hover:bg-emerald-600/30">Link Folder</button>
                    </>
                  ) : (
                    <>
                    <div className="flex-1">
                      <p className="text-xs text-zinc-300">{f.name}</p>
                      <p className="text-[9px] text-zinc-600">{f.size_human}</p>
                    </div>
                    <button onClick={() => { linkNASToItem(f.path); setShowNASPicker(false); }}
                      className="h-6 px-3 bg-blue-600 hover:bg-blue-500 text-white text-[9px] rounded-sm">Attach</button>
                    </>
                  )}
                </div>
              ))}
              {nasFiles.length === 0 && <p className="text-xs text-zinc-600 text-center py-6">Loading... (bridge agent responding)</p>}
            </div>
          </div>
        </div>
      )}
      {auditAssetId && (
        <AssetAuditTrailModal
          assetId={auditAssetId}
          api={api}
          onClose={() => setAuditAssetId(null)}
        />
      )}
    </div>
  );
}
